import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../data/blogPosts';
import { Clock, ArrowRight, BookOpen } from '../../components/icons';
import { updateBlogIndexMetadata } from '../../utils/seo';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';

const allTags = [...new Set(blogPosts.flatMap(p => p.tags))];

const BlogIndexPage: React.FC = () => {
  useEffect(() => { updateBlogIndexMetadata(); }, []);
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const filtered = activeTag
    ? blogPosts.filter(p => p.tags.includes(activeTag))
    : blogPosts;

  const [featured, ...rest] = filtered;

  return (
    <Layout maxWidth="max-w-6xl">
      <PageHeader icon={BookOpen} title="Color, Design & Accessibility" description="Practical guides on color theory, WCAG accessibility, and the science behind color." />

      {/* Tag filter pills */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setActiveTag(null)}
          aria-pressed={activeTag === null}
          className={`min-h-11 text-sm font-medium px-3 py-2 rounded-control border border-transparent transition-colors ${
            activeTag === null
              ? 'bg-brand-600 text-white'
              : 'bg-white border-surface-border text-ink-secondary hover:bg-surface-sunken'
          }`}
        >
          All
        </button>
        {allTags.map(tag => (
          <button
            key={tag}
            onClick={() => setActiveTag(tag === activeTag ? null : tag)}
            aria-pressed={tag === activeTag}
            className={`min-h-11 text-sm font-medium px-3 py-2 rounded-control border border-transparent transition-colors ${
              tag === activeTag
                ? 'bg-brand-600 text-white'
                : 'bg-white border-surface-border text-ink-secondary hover:bg-surface-sunken'
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Featured post */}
      {featured && (
        <Link
          to={`/blog/${featured.slug}`}
          className="ui-panel group block p-6 md:p-8 mb-6 hover:border-brand-300 transition-colors"
        >
          <div className="flex flex-wrap gap-1.5 mb-3">
            {featured.tags.map(tag => (
              <span
                key={tag}
                className="ui-badge"
              >
                {tag}
              </span>
            ))}
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-ink leading-tight mb-2 group-hover:text-brand-700 transition-colors">
            {featured.title}
          </h2>
          <p className="text-ink-secondary text-base leading-relaxed mb-4 max-w-3xl">
            {featured.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-muted">
            <span>{new Date(featured.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="flex items-center gap-1">
              <Clock size={14} aria-hidden="true" />
              {featured.readingTime} min read
            </span>
            <span className="ml-auto text-brand-600 flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all">
              Read <ArrowRight size={14} />
            </span>
          </div>
        </Link>
      )}

      {/* Remaining posts — 2 column */}
      <div className="grid gap-5 md:grid-cols-2">
        {rest.map(post => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="ui-panel group flex overflow-hidden hover:border-brand-300 transition-colors"
          >
            <div className="p-5 flex flex-col flex-1">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    className="ui-badge"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h2 className="text-ink font-semibold text-lg leading-snug mb-1.5 flex-1 group-hover:text-brand-700 transition-colors">
                {post.title}
              </h2>
              <p className="text-ink-secondary text-base leading-relaxed mb-3 line-clamp-2">{post.excerpt}</p>
              <div className="flex items-center gap-3 text-xs text-ink-muted mt-auto">
                <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                <span className="flex items-center gap-1">
                  <Clock size={14} aria-hidden="true" />
                  {post.readingTime} min
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Layout>
  );
};

export default BlogIndexPage;
